import React, { useState, useEffect } from 'react';
import { X, Loader2, FileText, Sparkles, Save, ChevronDown, ChevronUp, Trash2, Building2, Plus, Minus, Clock, ChevronRight, Copy } from 'lucide-react';
import LatteButton from '../common/LatteButton';
import Toast from '../common/Toast';
import { supabase } from '../../lib/supabase';

const CoverLetterModal = ({ isOpen, onClose, sessionId, userId, resumeData }) => {
  // 상태 관리
  const [activeView, setActiveView] = useState('new'); // 'new', 'detail', 'list'
  const [selectedCoverLetter, setSelectedCoverLetter] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  const [jobDescription, setJobDescription] = useState({
    company: '',
    position: '',
    companyInfo: '',
    responsibilities: '',
    requirements: ''
  });
  const [coverLetterTopics, setCoverLetterTopics] = useState(['']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContents, setGeneratedContents] = useState([]);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [savedCoverLetters, setSavedCoverLetters] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});
  const [newItemQuestion, setNewItemQuestion] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isGeneratingItem, setIsGeneratingItem] = useState(false);
  const [toast, setToast] = useState(null);

  // props 확인
  React.useEffect(() => {
    if (isOpen && !sessionId) {
      setError('세션 정보가 없습니다.');
    }
  }, [isOpen, sessionId]);

  // 저장된 자소서 목록 불러오기
  useEffect(() => {
    if (isOpen && sessionId) {
      fetchSavedCoverLetters();
    } else if (!isOpen) {
      // 모달이 닫힐 때 상태 초기화
      setActiveView('new');
      setSelectedGroup(null);
      setExpandedFields({});
    }
  }, [isOpen, sessionId]);

  // 저장된 자소서가 있으면 첫 번째 그룹 자동 선택
  useEffect(() => {
    if (savedCoverLetters.length > 0 && !selectedGroup && activeView === 'new') {
      setSelectedGroup(savedCoverLetters[0]);
      setActiveView('detail');
    }
  }, [savedCoverLetters]);

  // 저장된 자소서 목록 가져오기 (채용공고별로 그룹핑)
  const fetchSavedCoverLetters = async () => {
    setIsLoadingList(true);
    try {
      const { data, error } = await supabase
        .from('cover_letters')
        .select('*')
        .eq('session_id', sessionId)
        .is('deleted_at', null)  // 삭제되지 않은 항목만 조회
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // 채용공고별로 그룹핑 (job_description 내부에서 company와 position 추출)
      const grouped = (data || []).reduce((acc, item) => {
        const company = item.job_description?.company || '알 수 없음';
        const position = item.job_description?.position || '';
        const key = `${company}_${position}_${JSON.stringify(item.job_description)}`;
        
        if (!acc[key]) {
          acc[key] = {
            jobDescription: item.job_description,
            company: company,
            position: position,
            items: []
          };
        }
        acc[key].items.push(item);
        return acc;
      }, {});
      
      setSavedCoverLetters(Object.values(grouped));
    } catch (error) {
      console.error('자소서 목록 조회 실패:', error);
    } finally {
      setIsLoadingList(false);
    }
  };

  if (!isOpen) return null;

  // 세션 대화 내용 가져오기
  const fetchSessionConversations = async () => {
    try {
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Supabase 조회 에러:', error);
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('대화 내용 조회 실패:', error);
      throw error;
    }
  };

  // JD 입력값 변경 핸들러
  const handleJobDescriptionChange = (field, value) => {
    setJobDescription(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 자소서 항목 추가
  const addCoverLetterTopic = () => {
    setCoverLetterTopics([...coverLetterTopics, '']);
  };

  // 자소서 항목 삭제
  const removeCoverLetterTopic = (index) => {
    setCoverLetterTopics(coverLetterTopics.filter((_, i) => i !== index));
  };

  // 자소서 항목 변경
  const updateCoverLetterTopic = (index, value) => {
    const updated = [...coverLetterTopics];
    updated[index] = value;
    setCoverLetterTopics(updated);
  };

  // 백엔드 API를 통한 자소서 생성
  const generateCoverLetters = async () => {
    // 검증 로직
    if (!jobDescription.company.trim()) {
      setError('회사명을 입력해주세요.');
      return;
    }
    if (!jobDescription.position.trim()) {
      setError('채용 포지션을 입력해주세요.');
      return;
    }
    if (!jobDescription.companyInfo.trim()) {
      setError('포지션 정보를 입력해주세요.');
      return;
    }
    if (!jobDescription.responsibilities.trim()) {
      setError('담당업무를 입력해주세요.');
      return;
    }
    if (!jobDescription.requirements.trim()) {
      setError('자격요건을 입력해주세요.');
      return;
    }

    const validTopics = coverLetterTopics.filter(topic => topic.trim());
    if (validTopics.length === 0) {
      setError('최소 1개 이상의 자소서 항목을 입력해주세요.');
      return;
    }

    if (!sessionId) {
      setError('세션 정보를 찾을 수 없습니다.');
      console.error('sessionId가 없습니다:', { sessionId, userId });
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedContents([]);

    try {
      const conversations = await fetchSessionConversations();
      
      if (!conversations || conversations.length === 0) {
        setError('이 세션에 대화 내용이 없습니다. AI 컨설팅을 먼저 진행해주세요.');
        setIsGenerating(false);
        return;
      }
      
      const conversationText = conversations
        .map(msg => `${msg.sender === 'user' ? '사용자' : 'AI'}: ${msg.content}`)
        .join('\n\n');


      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
      
      const requestBody = {
        questions: validTopics,
        conversationHistory: conversationText.substring(0, 5000),
        resumeData: {
          name: resumeData?.name || '',
          title: resumeData?.title || '',
          summary: resumeData?.summary || '',
          experiencesCount: resumeData?.experiences?.length || 0,
          keyStrengths: Array.isArray(resumeData?.keyStrengths) ? resumeData.keyStrengths : []
        },
        jobDescription: jobDescription,
        userId: userId,
        sessionId: sessionId
      };


      const response = await fetch(`${backendUrl}/api/gemini/cover-letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API 호출 실패: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.results && Array.isArray(data.results)) {
        setGeneratedContents(data.results);
        
        await fetchSavedCoverLetters();
        setActiveView('result');
      } else if (data.content) {
        setGeneratedContents([{
          question: validTopics[0],
          answer: data.content
        }]);
        await fetchSavedCoverLetters();
        setActiveView('result');
      } else {
        console.error('예상치 못한 응답 형식:', data);
        setError('자소서 생성 응답 형식이 올바르지 않습니다.');
      }
      
    } catch (error) {
      console.error('자소서 생성 실패 상세:', error);
      
      let errorMessage = '자소서 생성 중 오류가 발생했습니다';
      if (error.message.includes('contents')) {
        errorMessage = '응답 데이터 형식 오류. 페이지를 새로고침하고 다시 시도해주세요.';
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // 저장된 자소서 삭제 (소프트 삭제)
  const deleteCoverLetter = async (id) => {
    try {
      // 소프트 삭제: deleted_at 필드에 현재 시간 설정
      const { error } = await supabase
        .from('cover_letters')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // 즉시 UI 업데이트 (낙관적 업데이트)
      if (selectedGroup) {
        const updatedItems = selectedGroup.items.filter(item => item.id !== id);
        const updatedGroup = { ...selectedGroup, items: updatedItems };
        
        // 현재 그룹 업데이트
        setSelectedGroup(updatedGroup);
        
        // 전체 목록에서도 업데이트
        setSavedCoverLetters(prev => 
          prev.map(group => 
            group === selectedGroup 
              ? updatedGroup 
              : group
          ).filter(group => group.items.length > 0) // 비어있는 그룹 제거
        );
        
        // 그룹의 모든 아이템이 삭제되었으면 새 작성 화면으로
        if (updatedItems.length === 0) {
          setSelectedGroup(null);
          setActiveView('new');
        }
      }
      
      // 백그라운드에서 전체 목록 새로고침
      fetchSavedCoverLetters();
      
      // 삭제 토스트 (빨간색)
      setToast({
        message: '자소서가 삭제되었습니다.',
        type: 'error'
      });
    } catch (error) {
      console.error('자소서 삭제 실패:', error);
      setToast({
        message: '삭제 중 오류가 발생했습니다.',
        type: 'error'
      });
    }
  };

  // 생성된 내용 복사
  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess({ ...copySuccess, [id]: true });
      setTimeout(() => {
        setCopySuccess(prev => ({ ...prev, [id]: false }));
      }, 2000);
      
      // 성공 토스트
      setToast({
        message: '클립보드에 복사되었습니다.',
        type: 'success'
      });
    } catch (err) {
      console.error('복사 실패:', err);
      setToast({
        message: '복사에 실패했습니다.',
        type: 'error'
      });
    }
  };

  // 새 자소서 작성 시작
  const startNewCoverLetter = () => {
    // 10개 제한 체크
    if (savedCoverLetters.length >= 10) {
      setToast({
        message: '자소서는 최대 10개까지만 작성할 수 있습니다.',
        type: 'warning'
      });
      return;
    }
    
    setActiveView('new');
    setSelectedCoverLetter(null);
    setSelectedGroup(null);
    setGeneratedContents([]);
    setCoverLetterTopics(['']);
    setJobDescription({
      company: '',
      position: '',
      companyInfo: '',
      responsibilities: '',
      requirements: ''
    });
    setExpandedFields({}); // 확장 상태 초기화
  };

  // 필드 확장/축소 토글
  const toggleFieldExpansion = (fieldName) => {
    setExpandedFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // 단일 자소서 항목 생성
  const generateSingleItem = async () => {
    if (!newItemQuestion.trim()) {
      setToast({
        message: '자소서 항목을 입력해주세요.',
        type: 'warning'
      });
      return;
    }

    setIsGeneratingItem(true);
    setError('');

    try {
      const conversations = await fetchSessionConversations();
      
      if (!conversations || conversations.length === 0) {
        setError('이 세션에 대화 내용이 없습니다. AI 컨설팅을 먼저 진행해주세요.');
        setIsGeneratingItem(false);
        return;
      }
      
      const conversationText = conversations
        .map(msg => `${msg.sender === 'user' ? '사용자' : 'AI'}: ${msg.content}`)
        .join('\n\n');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
      
      const requestBody = {
        questions: [newItemQuestion],
        conversationHistory: conversationText.substring(0, 5000),
        resumeData: {
          name: resumeData?.name || '',
          title: resumeData?.title || '',
          summary: resumeData?.summary || '',
          experiencesCount: resumeData?.experiences?.length || 0,
          keyStrengths: Array.isArray(resumeData?.keyStrengths) ? resumeData.keyStrengths : []
        },
        jobDescription: selectedGroup.jobDescription,
        userId: userId,
        sessionId: sessionId
      };

      const response = await fetch(`${backendUrl}/api/gemini/cover-letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API 호출 실패: ${response.status}`);
      }

      const data = await response.json();
      
      // 응답 데이터 확인
      if (data.success === false) {
        throw new Error(data.error || '자소서 생성 실패');
      }
      
      // 새로 생성된 항목을 현재 그룹에 즉시 추가 (낙관적 업데이트)
      if (data.results && data.results[0] && selectedGroup) {
        const newItem = {
          id: data.results[0].id || Date.now(), // 임시 ID
          question: newItemQuestion,
          answer: data.results[0].answer,
          created_at: new Date().toISOString(),
          job_description: selectedGroup.jobDescription
        };
        
        // 현재 그룹에 새 항목 추가
        const updatedGroup = {
          ...selectedGroup,
          items: [...selectedGroup.items, newItem]
        };
        
        setSelectedGroup(updatedGroup);
        
        // 전체 목록에서도 업데이트
        setSavedCoverLetters(prev => 
          prev.map(group => 
            group === selectedGroup ? updatedGroup : group
          )
        );
      }
      
      // 백그라운드에서 전체 목록 새로고침 (데이터 동기화)
      fetchSavedCoverLetters();
      
      // UI 상태 초기화
      setNewItemQuestion('');
      setIsAddingItem(false);
      
      // 성공 토스트
      setToast({
        message: '자소서 항목이 생성되었습니다.',
        type: 'success'
      });
      
    } catch (error) {
      console.error('자소서 항목 생성 실패:', error);
      setError('자소서 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGeneratingItem(false);
    }
  };



  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl w-full h-full max-w-7xl max-h-[95vh] overflow-hidden flex">
        
        {/* 좌측 사이드바 - 자소서 목록 */}
        <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col">
          {/* 사이드바 헤더 */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">
              자소서 관리 ({savedCoverLetters.length}/10)
            </h3>
            {savedCoverLetters.length < 10 ? (
              <button
                onClick={startNewCoverLetter}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Plus className="w-4 h-4" />
                새 자소서 작성
              </button>
            ) : (
              <div className="w-full px-4 py-2.5 bg-gray-400 text-white rounded-lg flex items-center justify-center gap-2 font-medium cursor-not-allowed">
                <Plus className="w-4 h-4" />
                작성 한도 초과 (최대 10개)
              </div>
            )}
          </div>

          {/* 자소서 목록 */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoadingList ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              </div>
            ) : savedCoverLetters.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">저장된 자소서가 없습니다</p>
                <p className="text-xs text-gray-400 mt-1">새 자소서를 작성해보세요</p>
              </div>
            ) : (
              <div className="space-y-2">
                {savedCoverLetters.map((group, groupIndex) => {
                  const groupKey = `group-${groupIndex}`;
                  const isSelected = selectedGroup === group;
                  
                  return (
                    <div 
                      key={groupKey} 
                      className={`bg-white rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedGroup(group);
                        setActiveView('detail');
                      }}
                    >
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium text-sm truncate ${
                              isSelected ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {group.company} | {group.position}
                            </h4>
                            <p className={`text-xs mt-1 ${
                              isSelected ? 'text-blue-600' : 'text-gray-400'
                            }`}>
                              {group.items.length}개 항목
                            </p>
                          </div>
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${
                            isSelected ? 'text-blue-500' : 'text-gray-400'
                          }`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 우측 메인 콘텐츠 영역 */}
        <div className="flex-1 flex flex-col bg-white">
          {/* 메인 헤더 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <FileText className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {activeView === 'new' ? '새 자소서 작성' : 
                   activeView === 'detail' ? '자소서 상세보기' :
                   activeView === 'result' ? '생성 결과' : '자소서 작성하기'}
                </h2>
                <p className="text-sm text-gray-500">
                  {activeView === 'new' ? '채용공고 정보를 입력하고 자소서를 생성하세요' :
                   activeView === 'detail' ? `${selectedGroup?.company} - ${selectedGroup?.position}` :
                   activeView === 'result' ? '생성된 자소서를 확인하세요' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="닫기"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* 메인 콘텐츠 */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeView === 'new' && (
              // 새 자소서 작성 화면
              <div className="max-w-4xl mx-auto space-y-6">
                {/* 상단 안내 메시지 */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">AI 자소서 생성 안내</h4>
                  </div>
                  <div className="space-y-2 text-sm text-blue-800">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">▸</span>
                      <p>이 자소서는 라떼이즈 컨설턴트와 나눈 이력서 작성 대화 내용과 완성된 이력서를 기반으로 생성됩니다.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">▸</span>
                      <p>라떼이즈 컨설턴트와 처음부터 이력서를 완성하신 경우 더욱 정확하고 개인화된 자소서를 생성할 수 있습니다.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">▸</span>
                      <p>AI가 생성한 내용은 참고용 초안입니다. 본인의 실제 경험과 감정을 추가하고 기업 문화에 맞게 다듬어 사용하세요.</p>
                    </div>
                  </div>
                </div>

                {/* 채용공고 정보 입력 */}
                <div className="space-y-4 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">채용공고 정보</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        회사 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={jobDescription.company}
                        onChange={(e) => handleJobDescriptionChange('company', e.target.value)}
                        placeholder="예: 네이버"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        disabled={isGenerating}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        채용 포지션 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={jobDescription.position}
                        onChange={(e) => handleJobDescriptionChange('position', e.target.value)}
                        placeholder="예: 프론트엔드 개발자"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      포지션 정보 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={jobDescription.companyInfo}
                      onChange={(e) => handleJobDescriptionChange('companyInfo', e.target.value)}
                      placeholder="예: 웹 서비스 개발팀, 신입/경력 3년 이상"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                      rows={2}
                      disabled={isGenerating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      담당업무 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={jobDescription.responsibilities}
                      onChange={(e) => handleJobDescriptionChange('responsibilities', e.target.value)}
                      placeholder="예: 네이버 메인 서비스 프론트엔드 개발, React 기반 웹 애플리케이션 개발 및 유지보수"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                      rows={3}
                      disabled={isGenerating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      자격요건 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={jobDescription.requirements}
                      onChange={(e) => handleJobDescriptionChange('requirements', e.target.value)}
                      placeholder="예: React, TypeScript 실무 경험 3년 이상, 대규모 서비스 개발 경험"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                      rows={3}
                      disabled={isGenerating}
                    />
                  </div>
                </div>

                {/* 자소서 항목 입력 */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">자소서 항목</h3>
                    <button
                      onClick={addCoverLetterTopic}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={isGenerating || coverLetterTopics.length >= 5}
                    >
                      <Plus className="w-4 h-4" />
                      항목 추가
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {coverLetterTopics.map((topic, index) => (
                      <div key={index} className="flex gap-2">
                        <textarea
                          value={topic}
                          onChange={(e) => updateCoverLetterTopic(index, e.target.value)}
                          placeholder={`자소서 항목 ${index + 1} (예: 지원동기 및 입사 후 포부를 작성해주세요.)`}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                          rows={2}
                          disabled={isGenerating}
                        />
                        {coverLetterTopics.length > 1 && (
                          <button
                            onClick={() => removeCoverLetterTopic(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            disabled={isGenerating}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>


                {error && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>
                )}

                {/* 생성 버튼 */}
                <div className="flex justify-end">
                  <LatteButton
                    onClick={generateCoverLetters}
                    disabled={isGenerating || !jobDescription.company.trim() || !jobDescription.position.trim() || !jobDescription.companyInfo.trim() || !jobDescription.responsibilities.trim() || !jobDescription.requirements.trim() || coverLetterTopics.filter(t => t.trim()).length === 0}
                    className="min-w-[150px]"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        생성 중...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        자소서 생성
                      </>
                    )}
                  </LatteButton>
                </div>
              </div>
            )}

            {activeView === 'detail' && selectedGroup && (
              // 자소서 상세보기 화면 - 그룹의 모든 항목 표시
              <div className="max-w-4xl mx-auto space-y-6">
                {/* 채용공고 정보 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        {selectedGroup.company}
                      </h2>
                      <p className="text-sm text-gray-600">{selectedGroup.position}</p>
                    </div>
                  </div>
                  
                  {selectedGroup.jobDescription && (
                    <div className="grid gap-3">
                      {selectedGroup.jobDescription.companyInfo && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h4 className="font-semibold text-gray-900 mb-1.5 flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            포지션 정보
                          </h4>
                          <div className="pl-3.5">
                            <p className={`text-sm text-gray-700 leading-relaxed ${
                              !expandedFields.companyInfo ? 'line-clamp-4' : ''
                            }`}>
                              {selectedGroup.jobDescription.companyInfo}
                            </p>
                            {selectedGroup.jobDescription.companyInfo.split('\n').length > 4 && (
                              <button
                                onClick={() => toggleFieldExpansion('companyInfo')}
                                className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
                              >
                                {expandedFields.companyInfo ? '접기' : '더보기'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {selectedGroup.jobDescription.responsibilities && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h4 className="font-semibold text-gray-900 mb-1.5 flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            담당업무
                          </h4>
                          <div className="pl-3.5">
                            <p className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap ${
                              !expandedFields.responsibilities ? 'line-clamp-4' : ''
                            }`}>
                              {selectedGroup.jobDescription.responsibilities}
                            </p>
                            {selectedGroup.jobDescription.responsibilities.split('\n').length > 4 && (
                              <button
                                onClick={() => toggleFieldExpansion('responsibilities')}
                                className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
                              >
                                {expandedFields.responsibilities ? '접기' : '더보기'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {selectedGroup.jobDescription.requirements && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h4 className="font-semibold text-gray-900 mb-1.5 flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            자격요건
                          </h4>
                          <div className="pl-3.5">
                            <p className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap ${
                              !expandedFields.requirements ? 'line-clamp-4' : ''
                            }`}>
                              {selectedGroup.jobDescription.requirements}
                            </p>
                            {selectedGroup.jobDescription.requirements.split('\n').length > 4 && (
                              <button
                                onClick={() => toggleFieldExpansion('requirements')}
                                className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
                              >
                                {expandedFields.requirements ? '접기' : '더보기'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 자소서 항목 헤더 및 추가 버튼 */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">자소서 항목 ({selectedGroup.items.length}/5)</h3>
                  {selectedGroup.items.length < 5 ? (
                    !isAddingItem && (
                      <button
                        onClick={() => setIsAddingItem(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        항목 추가
                      </button>
                    )
                  ) : (
                    <div 
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-400 text-white text-sm rounded-lg cursor-not-allowed"
                      onClick={() => {
                        setToast({
                          message: '자소서 항목은 최대 5개까지만 추가할 수 있습니다.',
                          type: 'warning'
                        });
                      }}
                      title="최대 5개까지만 추가 가능"
                    >
                      <Plus className="w-4 h-4" />
                      항목 한도 초과
                    </div>
                  )}
                </div>

                {/* 새 항목 추가 입력 영역 */}
                {isAddingItem && (
                  <div className="mb-4 p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        새 자소서 항목
                      </label>
                      <textarea
                        value={newItemQuestion}
                        onChange={(e) => setNewItemQuestion(e.target.value)}
                        placeholder="예: 지원동기 및 입사 후 포부를 작성해주세요."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                        rows={2}
                        disabled={isGeneratingItem}
                      />
                    </div>
                    {error && (
                      <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
                        {error}
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setIsAddingItem(false);
                          setNewItemQuestion('');
                          setError('');
                        }}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                        disabled={isGeneratingItem}
                      >
                        취소
                      </button>
                      <button
                        onClick={generateSingleItem}
                        disabled={isGeneratingItem || !newItemQuestion.trim()}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isGeneratingItem ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            생성 중...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            생성하기
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* 모든 자소서 항목 리스트 */}
                <div className="space-y-4">
                  {selectedGroup.items.map((item, index) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                      <div className="bg-gray-50 p-4 border-b border-gray-200 flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">
                            {index + 1}. {item.question}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(item.created_at).toLocaleString('ko-KR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleCopy(item.answer, item.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              copySuccess[item.id] 
                                ? 'bg-green-100 text-green-600' 
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                            title={copySuccess[item.id] ? '복사됨' : '복사'}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteCoverLetter(item.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 안내 메시지 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    💡 생성된 내용을 검토하고 필요에 따라 수정하여 사용하세요.
                    개인의 경험과 생각을 더 구체적으로 추가하면 더욱 좋은 자소서가 됩니다.
                  </p>
                </div>
              </div>
            )}

            {activeView === 'result' && (
              // 생성 결과 화면
              <div className="max-w-4xl mx-auto space-y-6">
                {/* 채용공고 정보 */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">
                      {jobDescription.company} - {jobDescription.position}
                    </span>
                  </div>
                  <p className="text-sm text-blue-800">
                    생성된 자소서가 목록에 자동으로 저장되었습니다.
                  </p>
                </div>

                {/* 생성된 자소서들 */}
                <div className="space-y-4">
                  {generatedContents.map((content, index) => (
                    <div key={index} className="border rounded-lg bg-white">
                      <div className="bg-gray-50 p-4 border-b">
                        <h4 className="font-medium text-gray-900">{content.question}</h4>
                      </div>
                      <div className="p-6">
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed mb-4">
                          {content.answer}
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleCopy(content.answer, `gen-${index}`)}
                            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                              copySuccess[`gen-${index}`] 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {copySuccess[`gen-${index}`] ? '✓ 복사됨' : '복사'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 안내 메시지 */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    ✅ 모든 자소서가 성공적으로 생성되고 저장되었습니다.
                    좌측 목록에서 확인하실 수 있습니다.
                  </p>
                </div>

                {/* 추가 작성 버튼 */}
                <div className="flex justify-center">
                  <button
                    onClick={startNewCoverLetter}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    새 자소서 작성
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Toast 메시지 - 모달 중앙 상단에 표시 */}
      {toast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60]">
          <Toast
            message={toast.message}
            type={toast.type}
            duration={3000}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </>
  );
};

export default CoverLetterModal;